<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import {
  transformMarkedText,
  splitKeywordParts,
  splitBySentence,
} from '@/composables/splitSentence';
import IconArrow15 from '@/components/svg/IconArrow15.vue';
import PersonalSpiderChart from '@/components/saju-insight/personality/PersonalSpiderChart.vue';
import SajuDaeun from '@/components/saju-insight/common/SajuDaeun.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const props = defineProps({
  yData: Object,
  isMobile: Boolean,
});
const { daeunCurNum, daeunsData } = storeToRefs(useAnalysisDataStore());
const bigLuckRadar = computed(() => props.yData?.분석상세?.대운흐름?.확률분포);
const radarChartData = computed(() => {
  const raw = bigLuckRadar.value || {};
  const result = {};
  for (const [key, val] of Object.entries(raw)) {
    result[key] = val.비율 ?? 0;
  }
  return result;
});
// 방향 전환점
const bigLuckFlow = computed(() => props.yData?.분석상세?.대운흐름?.운의방향); // 회색박스
const bigLuckFlowDesc = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.대운흐름?.대운흐름 || '');
  return sentences.map((sentence) => transformMarkedText(sentence, { keywordClass: 'y-keyword' }));
});
// 운의 방향 및 분포
const bigLuckDirectionOrder = computed(() => {
  return bigLuckFlow.value?.split('→') ?? [];
});
const directionColorClassMap = {
  남: 'five01',
  북: 'five02',
  동: 'five03',
  서: 'five04',
};
const directionChartSeries = computed(() => props.yData?.분석상세?.대운흐름?.방향분포);
const directionSegments = computed(() =>
  bigLuckDirectionOrder.value.map((dir) => {
    const count = directionChartSeries.value[dir] ?? 0;
    const width = count * 10;
    return {
      dir,
      count,
      widthPercent: `${width}%`,
      className: directionColorClassMap[dir] || '',
    };
  }),
);
// 전환상세
const changeDetail = computed(() => props.yData?.분석상세?.대운흐름?.전환상세);
const refinedTransitionList = computed(() => {
  return changeDetail.value.map(([ganji, age, from, to, year]) => ({
    age,
    from,
    to,
    year,
  }));
});

// 대운수
const bigLuckNum = computed(() => props.yData?.분석상세?.대운흐름?.대운수);
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">10</span>
          <span class="detail-tit__txt yongsin">대운흐름</span>
        </div>
        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="ys-img-0601-box">
            <PersonalSpiderChart
              :chartData="radarChartData"
              :isMobile="isMobile"
              :isYongsin="true"
            />
          </div>
          <div class="ys-content-box">
            <div class="content__title">방향전환점</div>
            <div class="ys-gray-line-box">{{ bigLuckFlow }}</div>
          </div>
          <div class="ys-exp-wrap">
            <div v-for="(html, idx) in bigLuckFlowDesc" :key="idx">
              <span class="text-box" v-html="html"></span>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper content_info_area_box">
          <div class="ys-content-box">
            <div class="content__title">운의 방향 및 분포</div>
            <div class="ys-direction-box">
              <template v-for="(dir, index) in bigLuckDirectionOrder" :key="dir">
                <span class="dir" :class="directionColorClassMap[dir]">{{ dir }}</span>
                <IconArrow15 v-if="index < bigLuckDirectionOrder.length - 1" />
              </template>
            </div>
            <div class="ys-direction-grape-box">
              <div
                v-for="item in directionSegments"
                :key="item.dir"
                class="dir"
                :class="item.className"
                :style="{ width: item.widthPercent }"
              >
                {{ item.dir }} {{ item.count }}
              </div>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="ys-content-box">
            <div class="content__title">전환상세</div>
            <div class="ys-circle-line-box">
              <div class="guide__circle-box">
                <div class="circle-box__overlap-group">
                  <div
                    class="yongsin-circle light overlap-circle"
                    v-for="(item, idx) in refinedTransitionList"
                    :key="idx"
                  >
                    <span class="circle__main-txt">{{ item.age }}세</span>
                    <span class="circle__sub-txt">{{ item.year }}년</span>
                    <span class="circle__dir-txt">
                      <em class="dir" :class="`txt-five01`">{{ item.from }}</em>
                      <!-- <IconArrow15 /> -->
                      <em class="">→</em>
                      <em class="dir" :class="`txt-five03`">{{ item.to }}</em>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ys-content-box">
            <div class="content__title">
              전체대운 <span class="txt-bold-red">{{ bigLuckNum }}대운</span>
            </div>
            <div class="summ-body__deaun">
              <SajuDaeun
                :pr-birth-data="daeunsData"
                :pr-lang-mode="langMode"
                :pr-type="'daeun'"
                :pr-cur-num="daeunCurNum"
              />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
.ys-circle-line-box {
  display: flex;
  padding: 20px 0;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  border-bottom: 1px solid var(--Gray-300, #ebebeb);
}
.personality-circle.st-ys-sub-03 {
  border: 1px solid rgba(78, 128, 214, 0.5);
  background: var(--gray-100-white, #fff);
  .circle__main-txt {
    color: #4e80d6;
  }
  .circle__sub-txt {
    color: var(--Gray-700, #575757);
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
  }
  &.bg-blue {
    border-radius: 200px;
    border: 1px solid rgba(78, 128, 214, 0.5);
    background: rgba(78, 128, 214, 0.05);
  }
  &.bg-transparent {
    background: transparent;
  }
  .circle__dir-txt {
    width: 100%;
    display: flex;
    gap: 15px;
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 160% */
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    .dir {
      &.txt-five01 {
        color: $fiveColor01;
      }
      &.txt-five02 {
        color: $fiveColor02;
      }
      &.txt-five03 {
        color: $fiveColor03;
      }
      &.txt-five04 {
        color: $fiveColor04;
      }
      &.txt-five05 {
        color: $fiveColor05;
      }
    }
  }
}
.ys-direction-grape-box {
  border-radius: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  .dir {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-100-white, #fff);
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px; /* 153.846% */
    &:first-child {
      border-radius: 20px 0 0 20px;
    }
    &:last-child {
      border-radius: 0 20px 20px 0;
    }
    &.five01 {
      background: $fiveColor01;
    }
    &.five02 {
      background: $fiveColor02;
    }
    &.five03 {
      background: $fiveColor03;
    }
    &.five04 {
      background: $fiveColor04;
    }
    &.five05 {
      background: $fiveColor05;
    }
  }
}
.ys-direction-box {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  .dir {
    display: flex;
    width: 40px;
    height: 40px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    color: var(--gray-100-white, #fff);
    font-family: Pretendard;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 30px; /* 136.364% */
    border-radius: 40px;
    &.five01 {
      background: $fiveColor01;
    }
    &.five02 {
      background: $fiveColor02;
    }
    &.five03 {
      background: $fiveColor03;
    }
    &.five04 {
      background: $fiveColor04;
    }
    &.five05 {
      background: $fiveColor05;
    }
  }
}
.ys-gray-line-box {
  display: flex;
  padding: 16px 20px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--Gray-200, #f8f8f8);
  color: var(--Gray-800, #333);
  font-family: Pretendard;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px; /* 136.364% */
}
</style>
